import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

const USER_SESSION_ID_STORAGE_KEY = 'rwcUtilsUserSessionId';

/**
 * This service provides a session id identifying the user across page
 * reloads until he closes all tabs. It is prefixed with Internal-
 * because we do not want to export the "setting" part of this service.
 */
@Injectable({
    providedIn: 'root'
})
export class InternalUserSessionService {
    private readonly location?: Location;
    private readonly sessionStorage?: Storage;

    constructor() {
        const window = inject(DOCUMENT, { optional: true })?.defaultView;
        this.location = window?.location;
        try {
            this.sessionStorage = window?.sessionStorage;
        } catch (e) {
            console.warn(
                'Error while getting access to window.sessionStorage. Check your browser settings to allow all cookies.',
                e
            );
        }
    }

    /**
     * Get the user session id or generate it if it doesn't yet exist
     */
    getId(): string {
        const idFromUrl = new URLSearchParams(this.location?.search).get('_tsessionid');
        const idFromStorage = this.sessionStorage?.getItem(USER_SESSION_ID_STORAGE_KEY);

        // 1st priority: ID from query params (keep using the same session id across spa navigations)
        // 2nd priority: ID from storage
        // 3rd priority: generate an ID
        const id = idFromUrl || idFromStorage || crypto.randomUUID();
        if (id !== idFromStorage && this.sessionStorage) {
            this.setId(id);
        }
        return id;
    }

    /**
     * Set the id in session storage
     * You should probably never need to use this. The user session should not be changed!
     * This method is only public, so the TracingService may use it.
     */
    setId(id: string): void {
        this.sessionStorage?.setItem(USER_SESSION_ID_STORAGE_KEY, id.toLowerCase());
        console.debug(`setting session storage [${USER_SESSION_ID_STORAGE_KEY}] to ${id}`);
    }
}
